import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Save } from "lucide-react";
import { euro, practiceApi } from "../../lib/practiceApi";
import { InstallmentRow } from "./InstallmentRow";
import { InvoiceDraftDialog } from "./InvoiceDraftDialog";
import { InvoiceReceiptDialog } from "./InvoiceReceiptDialog";
import "./billing.css";

const emptyRow = () => ({ id: "", _key: crypto.randomUUID(), label: "", amount_mode: "manual", amount: "", percent: null, event: "manuale", maturity_date: "", notes: "", status: "pending" });

export const BillingPlan = ({ practiceId, refreshKey, onChanged = () => {} }) => {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [draft, setDraft] = useState(null);
  const [receipt, setReceipt] = useState("");
  const dirtyRef = useRef(false);
  const requests = useRef(0);
  const lastPractice = useRef(practiceId);
  const markDirty = () => { dirtyRef.current = true; setDirty(true); setFlash(""); };
  const load = useCallback(async (force = false) => {
    const request = ++requests.current;
    try {
      const result = await practiceApi(`/billing/plan/${practiceId}`);
      if (request !== requests.current || (dirtyRef.current && !force)) return;
      setPlan(result); dirtyRef.current = false; setDirty(false); setError("");
    }
    catch (err) { setError(err.message); }
  }, [practiceId]);
  useEffect(() => {
    if (lastPractice.current !== practiceId) { lastPractice.current = practiceId; dirtyRef.current = false; setPlan(null); setDirty(false); }
    load();
    return () => { requests.current += 1; };
  }, [load, refreshKey, practiceId]);
  function change(index, values) { markDirty(); setPlan(p => ({ ...p, milestones: p.milestones.map((m, i) => i === index ? { ...m, ...values } : m) })); }
  async function save(event) {
    event.preventDefault(); if (busy) return; requests.current += 1; setBusy(true); setError(""); setFlash("");
    try {
      const result = await practiceApi(`/billing/plan/${practiceId}`, { method: "PUT", body: JSON.stringify({ version: plan.version || 0,
        milestones: plan.milestones.map(m => ({ id: m.id, label: m.label, amount_mode: m.amount_mode || "percentage",
          amount: m.amount === "" ? null : Number(m.amount), percent: m.percent == null || m.percent === "" ? null : Number(m.percent),
          event: m.event, maturity_date: m.maturity_date || "", notes: m.notes || "", status: m.status, recalculate: !!m.recalculate })) }) });
      setPlan(result); dirtyRef.current = false; setDirty(false); setFlash("Piano salvato. Importi e modalità delle rate confermati."); onChanged();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function prepare(row) {
    setBusy(true); setError("");
    try { setDraft(await practiceApi(`/billing/plan/${practiceId}/milestones/${row.id}/prepare`, { method: "POST" })); await load(); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  if (!plan) return <section className="practice-section" data-testid="billing-plan"><p data-testid="billing-plan-loading">{error || "Caricamento piano…"}</p></section>;
  const sum = Math.round(plan.milestones.reduce((total, row) => total + (["invoiced", "collected"].includes(row.status) || row.amount_mode === "manual" || (row.id && !row.recalculate) ? Number(row.amount || 0) : Math.round(plan.total * Number(row.percent || 0)) / 100), 0) * 100) / 100;
  const difference = Math.round((plan.total - sum) * 100) / 100;
  return <section className="practice-section" data-testid="billing-plan">
    <div className="practice-section-heading"><h2>Piano di fatturazione</h2></div>
    <div className="installment-plan-totals">
      <div><span>Importo incarico</span><strong data-testid="plan-total">{euro(plan.total)}</strong></div>
      <div><span>Totale piano</span><strong data-testid="plan-sum">{euro(sum)}</strong></div>
      <div><span>Differenza</span><strong data-testid="plan-difference">{euro(difference)}</strong></div>
    </div>
    {!plan.configured ? <div className="practice-empty" data-testid="plan-unconfigured"><p>Piano di fatturazione non configurato</p>
      <button type="button" className="primary" data-testid="plan-configure" onClick={() => { markDirty(); setPlan(p => ({ ...p, configured: true, milestones: [emptyRow()] })); }}>Configura piano</button>
    </div> : <form onSubmit={save} data-testid="installment-plan-form">
      {Math.abs(difference) >= 0.01 && <p className="practice-notice" role="alert" data-testid="plan-total-warning">{difference > 0 ? `Attenzione: il piano di fatturazione non copre completamente l’importo dell’incarico. Mancano ${euro(difference)}.` : `Attenzione: il piano di fatturazione supera l’importo dell’incarico di ${euro(-difference)}.`}</p>}
      {plan.milestones.map((row, index) => <InstallmentRow key={row.id || row._key} row={row} index={index} base={plan.total} busy={busy} dirty={dirty}
        onReceipt={() => setReceipt(row.draft_id)}
        onChange={values => change(index, values)} onPrepare={() => prepare(row)} onRemove={() => { markDirty(); setPlan(p => ({ ...p, milestones: p.milestones.filter((_, i) => i !== index) })); }} />)}
      <div className="billing-actions">
        <button type="button" className="ghost" disabled={busy} data-testid="plan-add" onClick={() => { markDirty(); setPlan(p => ({ ...p, milestones: [...p.milestones, emptyRow()] })); }}><Plus size={14} />Aggiungi quota</button>
        <button type="submit" className="primary" disabled={busy || !dirty} data-testid="plan-save"><Save size={14} />{busy ? "Salvataggio…" : "Salva piano"}</button>
        {dirty && <button type="button" className="ghost" data-testid="plan-cancel-changes" disabled={busy} onClick={() => load(true)}>Annulla modifiche</button>}
      </div>
    </form>}
    {error && <p className="error" role="alert" data-testid="plan-error">{error}</p>}
    {flash && <p className="flash" role="status" data-testid="plan-flash">{flash}</p>}
    {draft && <InvoiceDraftDialog draft={draft} onClose={() => setDraft(null)} onConfirmed={async () => { await load(); onChanged(); }} />}
    {receipt && <InvoiceReceiptDialog draftId={receipt} onClose={() => setReceipt("")} onSaved={async () => { await load(); onChanged(); }} />}
  </section>;
};